<template>
  <div data-test="checkout-success-output-one-content" class="output-one-confirmation">
    <div class="confirmation-card">
      <div class="card-header">
        <img v-if="confirmationHeaderImage" :src="confirmationHeaderImage" alt="Output One product logo" class="product-logo" />
        <h1 data-test="checkout-success-confirmation-header" class="card-title">Welcome to Output One</h1>
      </div>

      <template v-if="!isMobile">
        <div class="featured-section">
          <p class="eyebrow"><span class="eyebrow-star">✦</span> RECOMMENDED STARTER PAIR</p>
          <p class="featured-subtitle">Find, create, and play samples and instruments.</p>
          <div class="featured-grid">
            <div v-for="product in featuredDownloads" :key="product.id" class="featured-item">
              <div class="featured-item-header">
                <img v-if="product.meta.iconUrl" :src="product.meta.iconUrl" :alt="product.name" class="product-icon-lg" @error="handleIconError" />
                <div class="featured-item-info">
                  <h2 class="featured-item-name">{{ product.name }}</h2>
                  <p class="featured-item-tagline">{{ product.meta.tagline }}</p>
                </div>
              </div>
              <p class="featured-item-description">{{ product.meta.description }}</p>
              <ProductDownloadButton
                v-if="product.release"
                :handleDownload="(platform, release) => handleDownload(platform, release, product.id)"
                :release="product.release"
                :disabled="downloadingIds.has(product.id)"
                class="featured-download-btn"
              />
            </div>
          </div>
        </div>

        <div
          data-test="also-in-plan"
          class="also-in-plan"
          role="button"
          tabindex="0"
          :aria-expanded="isExpanded"
          @click="isExpanded = !isExpanded"
          @keydown.enter="isExpanded = !isExpanded"
          @keydown.space.prevent="isExpanded = !isExpanded"
        >
          <div class="also-in-plan-header">
            <span class="also-in-plan-title">Also in your plan</span>
            <span data-test="also-in-plan-chevron" class="also-in-plan-chevron">{{ isExpanded ? '↑' : '↓' }}</span>
          </div>

          <div class="also-in-plan-content" :class="{ 'is-expanded': isExpanded }">
            <div class="also-in-plan-track">
              <div data-test="also-in-plan-preview" class="also-in-plan-preview" :aria-hidden="isExpanded">
                <img
                  v-for="product in secondaryDownloads"
                  :key="product.id"
                  :src="product.meta.iconUrl"
                  :alt="product.name"
                  class="also-in-plan-preview-icon"
                  @error="handleIconError"
                />
              </div>

              <div data-test="also-in-plan-body" class="also-in-plan-body" :aria-hidden="!isExpanded" @click.stop>
                <div v-for="product in secondaryDownloads" :key="product.id" class="secondary-item">
                  <div class="secondary-item-info">
                    <img v-if="product.meta.iconUrl" :src="product.meta.iconUrl" :alt="product.name" class="product-icon-sm" @error="handleIconError" />
                    <div>
                      <p class="secondary-item-name">{{ product.name }}</p>
                      <p class="secondary-item-tagline">{{ product.meta.tagline }}</p>
                    </div>
                  </div>
                  <ProductDownloadButton
                    v-if="product.release"
                    :handleDownload="(platform, release) => handleDownload(platform, release, product.id)"
                    :release="product.release"
                    :disabled="downloadingIds.has(product.id)"
                    class="secondary-download-btn"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="mobile-section">
        <div class="mobile-icons">
          <img
            v-for="product in mobileIcons"
            :key="product.id"
            :src="product.meta.iconUrl"
            :alt="product.name"
            class="mobile-product-icon"
            @error="handleIconError"
          />
        </div>
        <div class="mobile-message">
          <p class="mobile-heading">On your phone?<br />Check your inbox for download links.</p>
          <p class="mobile-body">
            We sent an email to:<br />
            <strong class="mobile-email">{{ email }}</strong>
          </p>
        </div>
      </div>

      <div class="card-footer">
        <p>Visit your <a :href="accountPath" target="_blank" @click="handleAccountClick">Account</a> page to make updates and manage preferences.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useAmplitude, BrazeEvents } from '@outputinc/o-analytics'
import { useProductStore } from '@/store/ProductStore'
import { useDownloadStore } from '@/store/DownloadStore'
import { useUserStore } from '@/store/UserStore'
import { useBrazeStore } from '@/store/BrazeStore'
import { usePlanStore } from '@/store/PlanStore'
import ProductDownloadButton from '@/components/shared/screens/ProductDownloadButton.vue'
import { ClientRelease, ProductDownloadId } from '@/types'

// The two products shown prominently in the "Recommended Starter Pair" section.
// To change the featured pair, update this constant and open a PR.
const FEATURED_PAIR: readonly string[] = [ProductDownloadId.Arcade, ProductDownloadId.CoProducer]

// Static metadata keyed by product download ID (Storyblok slug).
// Icons are expected at /icons/products/{id}.png — add files to /public/icons/products/.
const PRODUCT_METADATA: Record<string, { tagline: string; description: string; iconUrl: string }> = {
  [ProductDownloadId.Arcade]: {
    tagline: 'Explore, play, & shape',
    description: 'Playable samples and instruments with an ever-growing library of loops, sounds, and kits.',
    iconUrl: '/icons/products/arcade.png'
  },
  [ProductDownloadId.CoProducer]: {
    tagline: 'Discover & create',
    description: 'AI-powered plugin that listens to your track and finds or creates samples that fit.',
    iconUrl: '/icons/products/co-producer.png'
  },
  [ProductDownloadId.Portal]: {
    tagline: 'Reverb & space',
    description: '',
    iconUrl: '/icons/products/portal.png'
  },
  [ProductDownloadId.Thermal]: {
    tagline: 'Saturation & warmth',
    description: '',
    iconUrl: '/icons/products/thermal.png'
  },
  [ProductDownloadId.Movement]: {
    tagline: 'Motion & texture',
    description: '',
    iconUrl: '/icons/products/movement.png'
  },
  [ProductDownloadId.Creator]: {
    tagline: 'Compose & arrange',
    description: '',
    iconUrl: '/icons/products/creator.png'
  }
}

const productStore = useProductStore()
const downloadStore = useDownloadStore()
const userStore = useUserStore()
const brazeStore = useBrazeStore()
const planStore = usePlanStore()

const { confirmationHeaderImage } = storeToRefs(productStore)
const { productDownloads } = storeToRefs(downloadStore)
const { outputUid, email } = storeToRefs(userStore)
const { selectedPlanProduct } = storeToRefs(planStore)
const { track, Events } = useAmplitude()
const { trackEmailUser } = brazeStore

const accountPath = import.meta.env.VITE_OUTPUT_ACCOUNTS_HOSTNAME

interface ProductMeta {
  tagline: string
  description: string
  iconUrl: string
}

interface ProductWithMeta {
  id: string
  name: string
  badge?: string
  release: ClientRelease | undefined
  meta: ProductMeta
}

const withMeta = (downloads: Omit<ProductWithMeta, 'meta'>[]): ProductWithMeta[] =>
  downloads.map(p => ({ ...p, meta: PRODUCT_METADATA[p.id] ?? { tagline: '', description: '', iconUrl: '' } }))

const mq = window.matchMedia('(max-width: 768px)')
const isMobile = ref(mq.matches)
const onMqChange = (e: MediaQueryListEvent) => {
  isMobile.value = e.matches
}
onMounted(() => mq.addEventListener('change', onMqChange))
onUnmounted(() => mq.removeEventListener('change', onMqChange))

const allDownloads = computed(() => withMeta(productDownloads.value))
const mobileIcons = computed(() => allDownloads.value.filter(p => p.meta.iconUrl))
const featuredDownloads = computed(() => withMeta(productDownloads.value.filter((p: Omit<ProductWithMeta, 'meta'>) => FEATURED_PAIR.includes(p.id))))
const secondaryDownloads = computed(() => {
  const items = productDownloads.value.filter((p: Omit<ProductWithMeta, 'meta'>) => !FEATURED_PAIR.includes(p.id))
  return withMeta([...items].sort((a, b) => (a.id === ProductDownloadId.Creator ? 1 : b.id === ProductDownloadId.Creator ? -1 : 0)))
})

const isExpanded = ref(false)

const handleIconError = (e: Event) => {
  if (e.target instanceof HTMLImageElement) {
    e.target.style.display = 'none'
  }
}

// Per-product in-flight guard — this page renders one button per download, so a
// single flag would disable them all. Reactive Set tracks .add/.delete.
const downloadingIds = reactive(new Set<string>())

const handleDownload = async (platform: string, release: ClientRelease, downloadId: string) => {
  const url = platform === 'win' ? release?.win.installer : release?.mac.installer

  if (!url) return

  // Guard against a double-click double-firing during the await below; reset in finally
  // since the installer downloads via Content-Disposition rather than navigating away.
  if (downloadingIds.has(downloadId)) return
  downloadingIds.add(downloadId)

  track(Events.WEBSITE_SUBSCRIPTION_APP_DOWNLOAD, {
    os: platform,
    products: [selectedPlanProduct.value],
    downloadId
  })

  // Await the Braze mutation before navigating — window.open('_self') tears down the
  // page and Safari/Firefox abort the in-flight POST if we fire-and-forget (WEB-7818).
  // Errors are already reported via BrazeStore.onTrackEmailError; catch so a rejection
  // doesn't surface as an unhandled rejection, and the download still runs.
  try {
    await trackEmailUser({
      externalId: outputUid.value,
      name: BrazeEvents.WEBSITE_APP_DOWNLOAD,
      time: new Date().toISOString(),
      properties: {
        os: platform,
        products: selectedPlanProduct.value,
        downloadId
      }
    })
  } catch {
    // reported via BrazeStore.onTrackEmailError
  } finally {
    window.open(url, '_self')
    downloadingIds.delete(downloadId)
  }
}

const handleAccountClick = () => {
  track(Events.CHECKOUT_SUBSCRIPTION_INSTALLATION_ACCOUNT_LINK_CLICK, { products: [selectedPlanProduct.value] })
}
</script>

<style lang="scss" scoped>
.output-one-confirmation {
  display: flex;
  justify-content: center;
  padding: $spacer-4 $spacer-4 $spacer-5;
  width: 100%;
}

.confirmation-card {
  background-color: $gray5;
  border-radius: 20px;
  box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.12);
  max-width: 760px;
  width: 100%;
  overflow: hidden;
  // Override inherited text-align: center from .screen-success
  text-align: left;
}

// ── Header ────────────────────────────────────────────────────────────────────

.card-header {
  // Linear gradient + two radial overlays for atmospheric depth (from Figma)
  background:
    radial-gradient(ellipse 140px 38px at 494px 71px, rgba(50, 60, 85, 0.4) 0%, rgba(25, 30, 43, 0.2) 27.5%, transparent 55%),
    radial-gradient(ellipse 151px 40px at 190px 133px, rgba(40, 50, 70, 0.3) 0%, transparent 45%), linear-gradient(174.42deg, #161a24 8.49%, #0d0e0f 91.51%);
  display: flex;
  flex-direction: column;
  align-items: center;
  // Matches Figma: logo at 16px top, 4px gap, title, 16px bottom = 204px total
  padding: 16px $spacer-4 16px;
  gap: 4px;
}

.product-logo {
  width: 72px;
  height: 72px;
  border-radius: 16px;
}

.card-title {
  font-family: $font-family-pilat-wide-heavy;
  font-size: 48px;
  line-height: 48px;
  color: $alabaster;
  text-align: center; // intentionally centered in header
  margin: 0;
  letter-spacing: 0.6px;
}

// ── Featured section ──────────────────────────────────────────────────────────

.featured-section {
  padding: $spacer-4;
}

.eyebrow {
  font-family: $font-family-pilat-bold;
  font-size: 16px;
  line-height: 20px;
  color: $yellow;
  letter-spacing: 0.5px;
  text-align: center; // intentionally centered
  margin: 0 0 $spacer-2;
}

.eyebrow-star {
  font-family: $font-family-pilat-book;
}

.featured-subtitle {
  font-family: $font-family-pilat-book;
  font-size: 16px;
  line-height: 22.5px;
  color: rgba(241, 231, 209, 0.6);
  text-align: center; // intentionally centered
  margin: 0 0 $spacer-4;
}

.featured-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;

  > .featured-item:not(:first-child) {
    border-left: 1px solid rgba(241, 231, 209, 0.1);
    padding-left: $spacer-4;
  }

  > .featured-item:not(:last-child) {
    padding-right: $spacer-4;
  }
}

.featured-item {
  display: flex;
  flex-direction: column;
  gap: $spacer-3;
}

.featured-item-header {
  display: flex;
  align-items: center;
  gap: $spacer-3;
}

.product-icon-lg {
  width: 72px;
  height: 72px;
  border-radius: 16px;
  flex-shrink: 0;
}

.featured-item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.featured-item-name {
  font-family: $font-family-pilat-bold;
  font-size: 36px;
  line-height: 1.1;
  color: $alabaster;
  margin: 0;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

.featured-item-tagline {
  font-family: $font-family-pilat-bold;
  font-size: 14px;
  line-height: 20px;
  color: rgba(241, 231, 209, 0.5);
  margin: 0;
}

.featured-item-description {
  font-family: $font-family-pilat-book;
  font-size: 14px;
  line-height: 21px;
  color: rgba(241, 231, 209, 0.45);
  margin: 0;
}

.featured-download-btn {
  width: 100%;
}

// ── Also in your plan (collapsible) ──────────────────────────────────────────

.also-in-plan {
  border-top: 1px solid rgba(241, 231, 209, 0.08);
  cursor: pointer;
  outline: none;

  &:focus-visible {
    outline: 2px solid rgba(241, 231, 209, 0.5);
    outline-offset: -2px;
  }
}

.also-in-plan-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacer-2;
  padding: $spacer-2 $spacer-4 $spacer-4;
  user-select: none;
}

.also-in-plan-title {
  font-family: $font-family-pilat-book;
  font-size: 13px;
  line-height: 20px;
  color: rgba(241, 231, 209, 0.5);
}

.also-in-plan-chevron {
  font-family: $font-family-pilat-book;
  font-size: 14px;
  line-height: 20px;
  color: rgba(241, 231, 209, 0.5);
}

// Viewport: clips the sliding track. Height transitions between two known content heights.
// Both values are deterministic given fixed icon sizes and $spacer-* padding.
//
// --preview-h is the single source of truth for the preview row height — the track
// must translateY by exactly this amount to bring the body into view. If icon size
// or padding changes, update --preview-h and the body height below; nothing else.
//
// preview: 54px icon + 16px padding-bottom ($spacer-3)                      = 70px
// body:    8px pt + 40px row + 24px gap ($spacer-4) + 40px row + 32px pb ($spacer-5) = 144px
.also-in-plan-content {
  --preview-h: 70px;

  overflow: hidden;
  height: var(--preview-h);
  transition: height 400ms cubic-bezier(0.4, 0, 0.2, 1);

  &.is-expanded {
    height: 144px;
  }
}

// Track: stacks preview above body. Slides up on expand so the body fills the viewport.
// translateY matches --preview-h exactly — the two are coupled by design.
.also-in-plan-track {
  transition: transform 400ms cubic-bezier(0.4, 0, 0.2, 1);

  .is-expanded & {
    transform: translateY(calc(var(--preview-h) * -1));
  }
}

.also-in-plan-preview {
  display: flex;
  justify-content: center;
  gap: $spacer-2;
  padding-bottom: $spacer-3;
}

.also-in-plan-preview-icon {
  width: 54px;
  height: 54px;
  border-radius: 12px;
  flex-shrink: 0;
}

.also-in-plan-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: $spacer-4;
  padding: $spacer-2 $spacer-4 $spacer-5;

  > .secondary-item:nth-child(odd) {
    padding-right: $spacer-4;
  }

  > .secondary-item:nth-child(even) {
    padding-left: $spacer-4;
  }
}

.secondary-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.secondary-item-info {
  display: flex;
  align-items: center;
  gap: $spacer-3;
}

.product-icon-sm {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  flex-shrink: 0;
}

.secondary-item-name {
  font-family: $font-family-pilat-bold;
  font-size: 18px;
  line-height: 22px;
  color: $alabaster;
  margin: 0;
  letter-spacing: 0.6px;
}

.secondary-item-tagline {
  font-family: $font-family-pilat-bold;
  font-size: 12px;
  line-height: 20px;
  color: rgba(241, 231, 209, 0.5);
  margin: 0;
}

.secondary-download-btn {
  width: 145px;
  height: 36px;
  font-size: 13px;
  flex-shrink: 0;
}

// ── Mobile section ────────────────────────────────────────────────────────────

.mobile-section {
  padding: $spacer-4;
  display: flex;
  flex-direction: column;
  gap: $spacer-4;
}

.mobile-icons {
  display: flex;
  gap: $spacer-2;
  justify-content: center;
  flex-wrap: wrap;
}

.mobile-product-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
}

.mobile-heading {
  font-family: $font-family-pilat-bold;
  font-size: 20px;
  line-height: 1.3;
  color: $alabaster;
  text-align: center;
  margin: 0 0 $spacer-2;
}

.mobile-body {
  font-family: $font-family-pilat-book;
  font-size: 16px;
  line-height: 24px;
  color: rgba(241, 231, 209, 0.55);
  text-align: center;
  margin: 0;
}

.mobile-email {
  color: $alabaster;
  font-family: $font-family-pilat-bold;
}

// ── Footer ────────────────────────────────────────────────────────────────────

.card-footer {
  border-top: 1px solid rgba(241, 231, 209, 0.08);
  padding: $spacer-4;
  text-align: center; // intentionally centered

  p {
    font-family: $font-family-pilat-book;
    font-size: 13px;
    line-height: 20px;
    color: rgba(241, 231, 209, 0.4);
    margin: 0;
  }

  a {
    color: rgba(241, 231, 209, 0.65);
    text-decoration: underline;
  }
}
</style>
